import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react';

import { useT } from '@/app/i18n/client';
import { useShowCalendarContext } from '@/features/product/contexts/ShowCalendarStoreContext';
import { cn } from '@/lib/utils';

import { Button } from '../ui/button';

interface IProps {
  title: string;
  checkInTime?: string;
  checkOutTime?: string;
  buttonLabel?: string;
  handleChangeMonth: (date: Date) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  currentMonth: Date;
  renderCalendar: () => React.JSX.Element[];
  isPrevMonthAvailable: boolean;
  isNextMonthAvailable: boolean;
}
export default function CommonCalendar({
  title,
  checkInTime,
  checkOutTime,
  buttonLabel,
  handleChangeMonth,
  currentMonth,
  renderCalendar,
  isPrevMonthAvailable,
  isNextMonthAvailable,
}: IProps) {
  const { t, i18n } = useT('product');

  const { showCalendarButtonRef, showCalendar, toggleCalendar } = useShowCalendarContext();

  return (
    <div className='mb-12'>
      <div className={cn('mb-6', checkInTime && checkOutTime && 'space-y-2')}>
        <h3 className='text-xl font-bold text-[#2E2E2E] max-sm:text-lg'>{title}</h3>
        {checkInTime && checkOutTime && (
          <div className='flex items-center gap-1'>
            <Clock className='h-[16px] w-[16px] text-[#0064FF]' />
            <p className='text-sm text-gray-500'>
              {t('checkIn')} <span className='font-bold'>{checkInTime}</span> ~ {t('checkOut')}{' '}
              <span className='font-bold'>{checkOutTime}</span>
            </p>
          </div>
        )}
      </div>

      <Button
        ref={showCalendarButtonRef}
        onClick={e => {
          e.stopPropagation();
          toggleCalendar();
        }}
        className='group flex items-center gap-2 rounded-2xl border-2 border-[#E9ECEF] bg-white px-4 py-4 text-sm font-semibold text-[#2E2E2E] hover:border-[#0064FF] hover:text-white'
      >
        <Calendar className='h-5 w-5 text-[#0064FF] group-hover:text-white' />
        <span className='whitespace-normal break-words text-left'>
          {buttonLabel || t('selectDatePlaceholder')}
        </span>
      </Button>

      {showCalendar && (
        <div className='mt-4 rounded-2xl border border-[#E9ECEF] bg-[#F8F9FA] p-5 max-sm:p-3'>
          <div className='mb-6 flex items-center justify-between'>
            <button
              onClick={
                isPrevMonthAvailable
                  ? handleChangeMonth(
                      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1),
                    )
                  : undefined
              }
              disabled={!isPrevMonthAvailable}
              className={cn(
                'rounded-xl p-3 transition-colors hover:bg-white',
                !isPrevMonthAvailable && 'opacity-0',
              )}
            >
              <ChevronLeft className='h-6 w-6 text-[#2E2E2E] max-sm:h-4 max-sm:w-4' />
            </button>
            <div className='flex flex-1 justify-center'>
              <h4 className='text-center text-base font-bold text-[#2E2E2E] sm:text-xl'>
                {currentMonth.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}
              </h4>
            </div>
            <button
              onClick={
                isNextMonthAvailable
                  ? handleChangeMonth(
                      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1),
                    )
                  : undefined
              }
              disabled={!isNextMonthAvailable}
              className={cn(
                'rounded-xl p-3 transition-colors hover:bg-white',
                !isNextMonthAvailable && 'opacity-0',
              )}
            >
              <ChevronRight className='h-6 w-6 text-[#2E2E2E] max-sm:h-4 max-sm:w-4' />
            </button>
          </div>
          <div className='mb-4 grid grid-cols-7 gap-2 max-sm:gap-1'>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div
                key={day}
                className='flex h-10 items-center justify-center text-base font-bold text-[#2E2E2E] max-sm:text-xs'
              >
                {day}
              </div>
            ))}
          </div>
          <div className='grid grid-cols-7 gap-2 max-sm:gap-1'>{renderCalendar()}</div>
        </div>
      )}
    </div>
  );
}
